import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useUser } from '@/hooks/auth/use-user';
import { useTaskContextTracking } from '@/hooks/tasks/useTaskContextTracking';

export interface CreateTaskWithAssigneesParams {
  title: string;
  goalId: string;
  description?: string;
  dueDate?: string;
  priority?: 'high' | 'medium' | 'not-important' | null;
  urgency?: 'high' | 'medium' | 'low' | null;
  assigneeIds?: string[];
}

export interface CreateTaskWithAssigneesResult {
  success: boolean;
  task_id?: string;
  assignees_added?: number;
  total_assignees_requested?: number;
  failed_assignments?: string[];
  message?: string;
  error?: string;
}

/**
 * Hook for creating tasks with assignees using atomic PostgreSQL function
 * Handles both task creation and assignee assignment in a single transaction
 */
export const useCreateTaskWithAssignees = () => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { trackTaskChange } = useTaskContextTracking();

  return useMutation({
    mutationFn: async (params: CreateTaskWithAssigneesParams): Promise<CreateTaskWithAssigneesResult> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Use supabase.rpc with any cast to avoid TypeScript errors temporarily
      const { data, error } = await (supabase as any).rpc('create_task_with_assignees', {
        p_title: params.title,
        p_goal_id: params.goalId,
        p_description: params.description || null,
        p_due_date: params.dueDate || null,
        p_priority: params.priority || null,
        p_urgency: params.urgency || null,
        p_assignee_ids: params.assigneeIds || []
      });

      if (error) {
        console.error('Failed to create task with assignees:', error);
        throw new Error(error.message);
      }

      if (!data) {
        throw new Error('No response from server');
      }

      // Parse the JSON response
      const result: CreateTaskWithAssigneesResult = typeof data === 'string' ? JSON.parse(data) : data;

      if (!result.success) {
        throw new Error(result.error || 'Task creation failed');
      }

      return result;
    },
    onSuccess: (result, params) => {
      // Invalidate project-specific task queries
      queryClient.invalidateQueries({ 
        queryKey: ['tasks', user?.id],
        exact: false 
      });
      
      // Invalidate TaskManager queries for current user and any assignees
      if (user?.id) {
        queryClient.invalidateQueries({ 
          queryKey: ['task-manager-tasks', user.id],
          exact: false 
        });
        queryClient.invalidateQueries({ 
          queryKey: ['assigned-tasks-count', user.id],
          exact: false 
        });
        
        // Since task creation with assignees can affect multiple users,
        // invalidate TaskManager caches broadly to ensure all assignees see updates
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            return (
              (Array.isArray(key) && key[0] === 'task-manager-tasks') ||
              (Array.isArray(key) && key[0] === 'assigned-tasks-count')
            );
          }
        });
      }
      
      // Invalidate goal queries if task was linked to a goal
      if (result.task_id) {
        queryClient.invalidateQueries({ 
          queryKey: ['goals', user?.id],
          exact: false 
        });
        
        // Invalidate task assignees queries for the new task
        queryClient.invalidateQueries({ 
          queryKey: ['task-assignees', result.task_id] 
        });
        
        // Track the task creation for context if task has a goal
        if (params.goalId) {
          trackTaskChange(result.task_id, 'create');
          console.log('[useCreateTaskWithAssignees] Tracked team task creation for context');
        }
      }

      // CREATE TASK ASSIGNMENT NOTIFICATIONS
      if (result.success && result.task_id && params.assigneeIds && params.assigneeIds.length > 0 && user?.id) {
        // Create notifications for all assignees asynchronously
        Promise.all([
          // Get task title
          supabase
            .from('planner_tasks')
            .select('title')
            .eq('id', result.task_id)
            .single(),
          // Get creator name  
          supabase
            .from('public_profile')
            .select('first_name, username')
            .eq('id', user.id)
            .single()
        ]).then(([taskResult, creatorResult]) => {
          if (taskResult.data && creatorResult.data) {
            const taskTitle = taskResult.data.title;
            const creatorName = creatorResult.data.first_name || creatorResult.data.username || 'Someone';
            
            // Create notification for each assignee (excluding creator)
            params.assigneeIds!.forEach(async (assigneeId: string) => {
              if (assigneeId !== user.id) {
                try {
                  await supabase.rpc('create_notification', {
                    p_user_id: assigneeId,
                    p_title: 'New Task Assignment',
                    p_message: `You have been assigned to "${taskTitle}" by ${creatorName}`,
                    p_type: 'info',
                    p_link: '/task-manager',
                    p_action_label: 'View Task',
                    p_source_type: 'task_assignment',
                    p_source_id: result.task_id
                  });
                  
                  console.log('[Notification] Task creation notification sent to assignee:', assigneeId);
                } catch (notificationError) {
                  console.error('[Notification] Failed to create assignment notification for user:', assigneeId, notificationError);
                }
              }
            });
          }
        }).catch((error) => {
          console.error('[Notification] Failed to fetch task/creator details for notifications:', error);
          // Don't fail the main operation for notification errors
        });
      }

      // Show appropriate success message
      if (result.message) {
        toast.success(result.message);
      } else {
        toast.success('Task created successfully!');
      }

      // Show warnings for failed assignments if any
      if (result.failed_assignments && result.failed_assignments.length > 0) {
        const failedNames = result.failed_assignments.join(', ');
        toast.warning(`Some assignments failed for: ${failedNames}`);
      }
    },
    onError: (error) => {
      console.error('Task creation failed:', error);
      toast.error(`Failed to create task: ${error.message}`);
    }
  });
}; 